import { useEffect, useState } from 'react'
import type { SubCustomer } from '../../domain/types'
import { SUB_CUSTOMER_PICKER_PAGE_SIZE, SUB_CUSTOMER_SEARCH_DEBOUNCE_MS } from '../../domain/constants'
import { subCustomersApi } from '../../infrastructure/api/sub-customers-api'

type Known = Record<string, SubCustomer>

const byDocumentId = (list: SubCustomer[]): Known => Object.fromEntries(list.map((c) => [c.documentId, c]))

/**
 * Server-side search for the picker (debounced; an empty query lists the first
 * page alphabetically), plus labels for ids picked earlier — e.g. restored from
 * the URL — that are not in the current results. Same self-fetching pattern as
 * SubCustomerSelector; the list API is tenant-isolated server-side.
 */
export function useSubCustomerOptions(query: string, selectedIds: string[]) {
  const [results, setResults] = useState<SubCustomer[]>([])
  const [loading, setLoading] = useState(false)
  const [known, setKnown] = useState<Known>({})

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const timer = setTimeout(async () => {
      try {
        const page = await subCustomersApi.getSubCustomers({ search: query.trim() || undefined, pageSize: SUB_CUSTOMER_PICKER_PAGE_SIZE })
        if (cancelled) return
        setResults(page.data)
        setKnown((prev) => ({ ...prev, ...byDocumentId(page.data) }))
      } catch {
        if (!cancelled) setResults([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, SUB_CUSTOMER_SEARCH_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query])

  const missing = selectedIds.filter((id) => !known[id]).join(',')
  useEffect(() => {
    if (!missing) return
    let cancelled = false
    const ids = missing.split(',')
    subCustomersApi
      .getSubCustomers({ documentIds: ids, pageSize: ids.length })
      .then((page) => !cancelled && setKnown((prev) => ({ ...prev, ...byDocumentId(page.data) })))
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [missing])

  return { results, loading, known }
}
